import React, { useState } from 'react';
import { Shield, FileText, Copy, Check, AlertTriangle, Info } from 'lucide-react';
import { PIIScanner } from '../components/pii';

const sampleTexts = [
  {
    title: 'Witness Statement',
    text: `Witness statement from John Smith, DOB 03/15/1985, SSN 123-45-6789. 
Contact at 555-123-4567 or john.smith@email.com. 
Incident occurred at 742 Evergreen Terrace, Springfield on January 15, 2026.
Suspect paid with credit card ending 4532.`
  },
  {
    title: 'Police Report',
    text: `Officer Badge #4521 responding to call at 1234 Oak Street, Baltimore MD 21201.
Victim: Sarah Mitchell, phone 443-555-0199, email sarah.m@gmail.com.
Suspect identified as Michael Brown, SSN 987-65-4321, DOB 08/22/1990.
Vehicle registration: 4AB-C123, registered to defendant at 567 Pine Ave.`
  },
  {
    title: 'Financial Evidence',
    text: `Transaction record for account holder James Wilson.
Bank Account: 1234567890, Routing: 021000021
Credit Card: 4111-1111-1111-1111 (Exp: 03/27)
Wire transfer to offshore account from IP address 192.168.1.100.
Contact: jwilson@corporation.com, +1 (202) 555-0147`
  }
];

export const PIIDemoPage: React.FC = () => {
  const [selectedSample, setSelectedSample] = useState(0);
  const [copied, setCopied] = useState(false);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">PII Detection & Redaction Demo</h1>
            <p className="text-gray-600">Azure AI Language - Personally Identifiable Information Detection</p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">How PII Redaction Works in DEMS</p>
          <ul className="list-disc list-inside space-y-1 text-blue-700">
            <li>Evidence text is scanned using <strong>Azure AI Language</strong></li>
            <li>Detected PII includes: Names, SSNs, Phone Numbers, Emails, Addresses, Financial Data</li>
            <li>Three redaction modes: <code className="bg-blue-100 px-1 rounded">[ENTITY_TYPE]</code>, <code className="bg-blue-100 px-1 rounded">****</code>, or remove</li>
            <li>Original + redacted versions stored for role-based access control</li>
          </ul>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sample Texts */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-4">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Sample Evidence Texts
            </h3>
            <div className="space-y-2">
              {sampleTexts.map((sample, idx) => (
                <button
                  key={idx}
                  onClick={() => setSelectedSample(idx)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedSample === idx
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{sample.title}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(sample.text);
                      }}
                      className="p-1 hover:bg-gray-200 rounded"
                      title="Copy to clipboard"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{sample.text}</p>
                </button>
              ))}
            </div>

            {/* PII Categories Legend */}
            <div className="mt-6 pt-4 border-t">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Detected Categories</h4>
              <div className="space-y-1 text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                  <span>Person Names</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-500"></span>
                  <span>SSN / Financial</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-500"></span>
                  <span>Phone Numbers</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-purple-500"></span>
                  <span>Email Addresses</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-orange-500"></span>
                  <span>Physical Addresses</span>
                </div>
              </div>
            </div>
          </div>

          {/* Demo Tips */}
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Demo Tips</p>
                <ul className="mt-1 space-y-1 text-amber-700">
                  <li>• Select a sample text to test detection</li>
                  <li>• Try different redaction modes</li>
                  <li>• Toggle between original/redacted views</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Scanner Component */}
        <div className="lg:col-span-2">
          <PIIScanner 
            initialText={sampleTexts[selectedSample].text}
            onScanComplete={(result) => {
              console.log('PII Scan complete:', result);
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default PIIDemoPage;
